package co.maytes.standupbot.dashboard.components.motion

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.styles.Motion
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.classNames
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.silk.components.text.SpanText
import kotlinx.coroutines.delay
import kotlin.math.max
import kotlin.math.roundToInt

private const val COUNT_UP_DUR_MS = 700

@Composable
fun FadeInOnMount(
    key: Any? = Unit,
    delayMs: Int = 0,
    contentAlignment: Alignment = Alignment.TopStart,
    content: @Composable () -> Unit,
) {
    Box(
        modifier = Modifier
            .fillMaxWidth()
            .classNames(Motion.ENTER_CLASS)
            .styleModifier {
                if (delayMs > 0) property("animation-delay", "${delayMs}ms")
            },
        contentAlignment = contentAlignment,
    ) {
        @Suppress("UNUSED_EXPRESSION") key
        content()
    }
}

@Composable
fun <T> Staggered(
    items: List<T>,
    key: (T) -> Any = { it!! },
    stepMs: Int = Motion.STAGGER_STEP_MS,
    animate: Boolean = true,
    content: @Composable (T) -> Unit,
) {
    items.forEachIndexed { index, item ->
        val delay = (index * stepMs).coerceAtMost(Motion.STAGGER_CAP_MS)
        androidx.compose.runtime.key(key(item)) {
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .let { base ->
                        if (animate) {
                            base.classNames(Motion.ENTER_CLASS).styleModifier {
                                if (delay > 0) property("animation-delay", "${delay}ms")
                            }
                        } else {
                            base
                        }
                    },
            ) {
                content(item)
            }
        }
    }
}

@Composable
fun CrossFade(
    key: Any?,
    content: @Composable () -> Unit,
) {
    androidx.compose.runtime.key(key) {
        Box(modifier = Modifier.classNames("sb-enter-fade")) {
            content()
        }
    }
}

@Composable
fun CountUp(
    value: Int,
    modifier: Modifier = Modifier,
    durationMs: Int = COUNT_UP_DUR_MS,
    format: (Int) -> String = { it.toString() },
) {
    var displayed by remember { mutableStateOf(value) }
    val from = remember(value) { displayed }

    LaunchedEffect(value) {
        if (value == from || prefersReducedMotion()) {
            displayed = value
            return@LaunchedEffect
        }
        val start = now()
        val total = max(durationMs, 16)
        while (true) {
            val elapsed = (now() - start).toInt()
            if (elapsed >= total) {
                displayed = value
                break
            }
            val t = elapsed.toDouble() / total
            val eased = 1 - (1 - t) * (1 - t) * (1 - t)
            displayed = (from + (value - from) * eased).roundToInt()
            delay(COUNT_UP_FRAME_MS)
        }
    }

    SpanText(text = format(displayed), modifier = modifier)
}

private fun now(): Double = kotlinx.browser.window.performance.now()
private const val COUNT_UP_FRAME_MS = 16L

fun prefersReducedMotion(): Boolean =
    kotlinx.browser.window.matchMedia(Motion.REDUCED_MOTION_QUERY).matches ||
        kotlinx.browser.document.documentElement?.classList?.contains(Motion.FORCE_REDUCED_MOTION_CLASS) == true
